import { type FC, type PropsWithChildren, type ReactElement, type ReactNode } from 'react';
import { connect } from 'react-redux';

import type { ITableConfigParserOptions } from '../../api/fetchers/tableConfig/types';
import { TranslationProvider } from '../../api/useTranslation';
import { GameConfigProvider } from '../../config/GameConfigProvider';
import { AudioControllerConnected } from '../../controllers/audioController/AudioControllerConnected';
import type { SoundResourceType } from '../../controllers/audioController/types';
import { BrowserCacheControllerConnector } from '../../controllers/browserCacheController/BrowserCacheControllerConnector';
import type { BrowserCacheControllerProps } from '../../controllers/browserCacheController/types';
import { ChatCommunicationControllerConnector } from '../../controllers/chatCommunication/ChatCommunicationControllerConnector';
import {
  CrossOriginCommunicationControllerConnector,
  CrossOriginCommunicationControllerMessengerConnected,
} from '../../controllers/crossOriginCommunication/CrossOriginCommunicationControllerConnector';
import { GameCommunicationControllerConnector } from '../../controllers/gameCommunication/GameCommunicationControllerConnector';
import type { GameCommunicationControllerConfig } from '../../controllers/gameCommunication/types';
import { PerformanceControllerConnector } from '../../controllers/performanceController/PerformanceControllerConnector';
import { LobbyMessageReceiver } from '../../messages/LobbyMessageReceiver';
import { MessageConnector } from '../../messages/MessageConnector';
import { coreSoundResources } from '../../sounds';
import { type CoreRootState, CoreStoreProvider, CoreStoreType } from '../../store/CoreStore';
import type { ICoreTimings } from '../../store/slices/config/types';
import { settingsActions } from '../../store/slices/settings/slice';
import type { IChatConfig } from '../chat';
import { FinalErrorBoundary } from '../errorBoundary/FinalErrorBoundary';
import { LayoutErrorBoundaryConnected } from '../errorBoundary/LayoutErrorBoundaryConnected';
import type { TrafficLightConfigType } from '../game/trafficLight/types';
import { GameBody } from '../gameBody/GameBody';
import type { HelpConfigType } from '../help/types';
import { IconRoot } from '../icons/iconRoot';
import type { NavBurgerPlainProps } from '../nav/navBurger/NavBurger';
import { PopupConnected } from '../popup/PopupConnected';
import type { VolumeChannelControlType } from '../settings/VolumeChannelControlConnected';
import type { SubtitlesConfig } from '../subtitles/types';
import type { TabProps } from '../tabs/Tabs';
import { VideoRefContextProvider } from '../video_v8/VideoRefContext';

import { GameInitializer } from './GameInitialiser';

export interface IMainConfig {
  betConfig: number[];
  helpConfig: HelpConfigType;
  languages: string[];
  playAreaSize: number;
  uiVolumeChannel: string;
  tableConfigOptions: ITableConfigParserOptions;
  volumeChannels: Record<string, string>;
  gameHeaderOverlayHeight?: number;
}

export interface GameConfigType extends IMainConfig {
  soundResources: SoundResourceType[];
  gameStore: CoreStoreType;
  defaultTimings: ICoreTimings;
  trafficLightConfig: Record<string, TrafficLightConfigType>;
  chatConfig?: IChatConfig;
  gameCommunicationConfig?: GameCommunicationControllerConfig;
  subtitlesConfig?: SubtitlesConfig;
  overridingChipColorCodes?: string[];
  resources?: BrowserCacheControllerProps['resources'];
  TotalBetComponent?: FC;
}

export interface GameConfigurationInterface {
  betLimitContent: ReactNode;
  config: GameConfigType;
  gameSettingsSpecificTab?: TabProps[];
  navControls?: NavBurgerPlainProps['navControls'];
  quickGuideContent?: ReactNode[];
  soundControls: VolumeChannelControlType[];
}

export interface GameRootProps extends PropsWithChildren, GameConfigurationInterface {
  background: ReactNode;
}

const TranslationProviderConnected = connect(
  (state: CoreRootState) => ({
    currentLanguage: state.settings.language.current,
  }),
  {
    setLanguage: settingsActions.setLanguage,
  }
)(TranslationProvider);

export const GameRoot = ({
  background,
  betLimitContent,
  children,
  config,
  gameSettingsSpecificTab,
  navControls,
  quickGuideContent,
  soundControls,
}: GameRootProps): ReactElement => {
  const { gameCommunicationConfig, gameStore, resources, soundResources } = config;

  return (
    <FinalErrorBoundary>
      <CoreStoreProvider store={gameStore}>
        <TranslationProviderConnected>
          <VideoRefContextProvider>
            <LayoutErrorBoundaryConnected>
              <GameConfigProvider
                value={{
                  betLimitContent,
                  config,
                  gameSettingsSpecificTab,
                  navControls,
                  quickGuideContent,
                  soundControls,
                }}
              >
                {background}
                <GameInitializer />
                <IconRoot />
                <AudioControllerConnected audioFiles={[...coreSoundResources, ...soundResources]} />
                <GameCommunicationControllerConnector gameStore={gameStore} config={gameCommunicationConfig} />
                <ChatCommunicationControllerConnector gameStore={gameStore} />
                <CrossOriginCommunicationControllerConnector gameStore={gameStore} />
                <CrossOriginCommunicationControllerMessengerConnected />
                <BrowserCacheControllerConnector resources={resources} />
                <PerformanceControllerConnector />
                <GameBody>{children}</GameBody>
                <PopupConnected />
                <MessageConnector />
                <LobbyMessageReceiver />
              </GameConfigProvider>
            </LayoutErrorBoundaryConnected>
          </VideoRefContextProvider>
        </TranslationProviderConnected>
      </CoreStoreProvider>
    </FinalErrorBoundary>
  );
};
